import "./css/Main.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import DefaultContainer from "./DefaultContainer.js";
import * as urlPaths from "./enums/UrlPaths";
import { CssBaseline } from "@mui/material";
import { useTheme } from "@emotion/react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CookieConsent from "react-cookie-consent";
import Loading from "./contexts/Loading";
import TermsAndConditionsPage from "./features/termsAndConditions/TermsAndConditionsPage";
import "./utils/i18n/config";
import VerifyPage from "./features/auth/verify/VerifyPage";
import ResendVerificationMailPage from "./features/auth/resendVerification/ResendVerificationMailPage";
import { setDefaultOptions } from "date-fns";
import { enGB } from "date-fns/locale";
import updateLocale from "dayjs/plugin/updateLocale";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import ForgottenPasswordPage from "./features/auth/forgotPassword/ForgottenPasswordPage";
import ChangePasswordPage from "./features/auth/changePassword/ChangePasswordPage";
import NewEmailConfirmationPage from "./features/auth/newEmailConfirmation/NewEmailConfirmationPage";
import ChangeEmailViewPage from "./features/auth/changeEmail/ChangeEmailViewPage";
import EmployeeActivationPage from "./features/auth/employeeActivation/EmployeeActivationPage.jsx";
import UserActivationPage from "./features/auth/userActivation/UserActivationPage.jsx";
import UserActivationConfirmPage from "./features/auth/userActivation/UserActivationConfirmPage.jsx";
import { GoogleOAuthProvider } from "@react-oauth/google";
import LoginPage from "./features/auth/login/LoginPage";
import SignUpPage from "./features/auth/signup/SignUpPage";
import { CustomThemeProvider } from "./contexts/ThemeContext.jsx";
import { Snackbar, SnackbarContext } from "./contexts/Snackbar.jsx";
import HeaderNoAuth from "./components/display/HeaderNoAuth.jsx";
import QrRedirectPage from "./features/admin/clinic/components/qrCodes/QrRedirectPage.jsx";
import DeleteAccountPage from "./features/auth/deleteAccount/DeleteAccountPage.jsx";
import DeleteAccountConfirmationPage from "./features/auth/deleteAccount/DeleteAccountConfirmationPage.jsx";

// Sets big calendar to start with monday
setDefaultOptions({ locale: enGB });

//Sets date picker to start with monday
dayjs.extend(customParseFormat);
dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  weekStart: 1,
});

function App() {
  const outerTheme = useTheme();
  const { t } = useTranslation();

  return (
    <Router future={{ v7_startTransition: true }}>
      <CustomThemeProvider>
        <CssBaseline enableColorScheme />
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_LOGIN_ID}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={{
              previousMonth: t("PreviousMonth"),
              nextMonth: t("NextMonth"),
            }}
          >
            <Snackbar>
              <Loading>
                <Routes>
                  <Route
                    index
                    exact
                    path={urlPaths.LOGIN}
                    element={<LoginPage />}
                  />
                  <Route
                    index
                    path={urlPaths.CLINIC_LOGIN}
                    element={<LoginPage />}
                  />
                  <Route
                    exact
                    path={urlPaths.SIGN_UP}
                    element={<SignUpPage />}
                  />
                  <Route
                    exact
                    path={urlPaths.TERMS}
                    element={<TermsAndConditionsPage />}
                  />
                  <Route
                    exact
                    path={urlPaths.VERIFY}
                    element={<VerifyPage />}
                  />
                  <Route
                    exact
                    path={urlPaths.RESEND_VERIFICATION_EMAIL}
                    element={
                      <HeaderNoAuth>
                        <ResendVerificationMailPage />
                      </HeaderNoAuth>
                    }
                  />
                  <Route
                    exact
                    path={urlPaths.EMPLOYEE_ACTIVATION}
                    element={
                      <HeaderNoAuth>
                        <EmployeeActivationPage />
                      </HeaderNoAuth>
                    }
                  />
                  <Route
                    exact
                    path={urlPaths.USER_ACTIVATION}
                    element={
                      <HeaderNoAuth>
                        <UserActivationPage />
                      </HeaderNoAuth>
                    }
                  />
                  <Route
                    exact
                    path={urlPaths.USER_ACTIVATION_CONFIRM}
                    element={
                      <HeaderNoAuth>
                        <UserActivationConfirmPage />
                      </HeaderNoAuth>
                    }
                  />
                  <Route
                    exact
                    path={urlPaths.FORGOTTEN_PASSWORD}
                    element={
                      <HeaderNoAuth>
                        <ForgottenPasswordPage />
                      </HeaderNoAuth>
                    }
                  />
                  <Route
                    exact
                    path={urlPaths.ACCOUNT_DELETE_REQUEST_PAGE}
                    element={
                      <DeleteAccountPage/>
                    }
                  />
                  <Route
                    exact
                    path={urlPaths.ACCOUNT_DELETE_CONFIRMATION_PAGE}
                    element={
                      <DeleteAccountConfirmationPage/>
                    }
                  />
                  <Route
                    path={urlPaths.NEW_EMAIL_CONFIRMATION}
                    element={<NewEmailConfirmationPage />}
                  />
                  <Route
                    path={urlPaths.CHANGE_USER_EMAIL}
                    element={<ChangeEmailViewPage />}
                  />
                  <Route
                    exact
                    path={urlPaths.CHANGE_FORGOTTEN_PASSWORD}
                    element={
                      <HeaderNoAuth>
                        <ChangePasswordPage />
                      </HeaderNoAuth>
                    }
                  />
                  <Route
                    path={urlPaths.QR_REDIRECT_ID}
                    element={
                      <HeaderNoAuth>
                        <QrRedirectPage />
                      </HeaderNoAuth>
                    }
                  />

                  <Route path="*" element={<DefaultContainer />} />
                </Routes>
                <CookieConsent
                  sameSite="strict"
                  location="bottom"
                  buttonText="Accept"
                  enableDeclineButton
                  disableStyles
                  declineButtonText="Decline"
                  cookieName="cookie"
                  containerClasses="cookieBanner"
                  buttonClasses="cookieButton cookieAccept"
                  declineButtonClasses="cookieButton cookieDecline"
                >
                  <div className="cookieText">{t("CookieText")}</div>
                </CookieConsent>
              </Loading>
            </Snackbar>
          </LocalizationProvider>
        </GoogleOAuthProvider>
      </CustomThemeProvider>
    </Router>
  );
}

export default App;
